import {
    CustomRenderType,
    ExportTypeType,
    ExtendOptionsType,
    FilterOperatorType,
    GetAllPrintRowsType,
    GridRequestType,
    SelectOptionsType,
    SelectRowType,
    TableColumnType,
    TablePropsType,
    TableRowType
} from 'src/types/table';
import { isUndefinedNull } from 'src/utils/isDefined';
import moment from 'moment';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import translate from '@translate';
import { downloadCSV, downloadXLSX } from 'src/utils/downloadFile';
import exportFile from 'src/utils/exportFile';
import api from '@api';
import { WindowSizeType } from 'src/utils/useWindowSize';
import { getDateFormat, getDateTimeFormat, shortDateFormat } from 'src/utils/timeConstants';
import { formatNumber, isNumericValue } from 'src/utils/formatNumber';
import { FILTER_ARR_END_CHAR, FILTER_ARR_START_CHAR } from './constants';
import { SelectValueType } from '../Select';
import { TextFieldValueType } from '../TextField';
import React, { ReactNode } from 'react';
import colorOptionFce, { isBoolLikeSelectOption } from 'src/utils/colorOptionFce';

const MAX_EXPORT_ITEMS = 5000;

export type FormatColumnOptions = {
    formatDisplay?: boolean;
};

const shouldFormatDisplayNumber = (column: TableColumnType): boolean => column.type === 'number-filter';

export const formatColumnText = (
    column: TableColumnType,
    row: TableRowType,
    selectOptions?: { [key: string]: ConstantsCodeType[] },
    options?: FormatColumnOptions
) => {
    let rowValue = row[column.displayKey || column.key];
    if (isUndefinedNull(rowValue)) {
        rowValue = '';
    }
    switch (column.type) {
        case 'select':
            if (rowValue !== '' && selectOptions && column.selectOption && selectOptions[column.selectOption]) {
                const optionKey = column.selectOptionKeyFce ? column.selectOptionKeyFce(row) : rowValue;
                const selectedOption = selectOptions[column.selectOption].find((item) => item.value === optionKey);
                if (selectedOption) {
                    rowValue = selectedOption.label;
                }
            }
            break;
        case 'multi-select':
            if (rowValue && column.selectOption) {
                rowValue = rowValue
                    .map((item: string) => {
                        // @ts-ignore
                        const selectedOption = selectOptions?.[column.selectOption].find(
                            (option: ConstantsCodeType) => option.value === item
                        );
                        return selectedOption?.label || item;
                    })
                    .join(', ');
            }
            break;
        case 'date':
            if (rowValue) {
                if (String(Number(rowValue)) === rowValue) {
                    rowValue = moment(rowValue).format(shortDateFormat);
                } else {
                    rowValue = moment(rowValue).format(getDateFormat());
                }
            }
            break;
        case 'dateTime':
            if (rowValue) {
                if (String(Number(rowValue)) === rowValue) {
                    rowValue = moment(rowValue).format(shortDateFormat);
                } else {
                    rowValue = moment(rowValue).format(getDateTimeFormat());
                }
            }
            break;
        case 'number-filter':
        case 'number':
            rowValue = Number(rowValue);
            break;
        default:
            break;
    }

    // little hack that number columns from order with products export have correct format
    if (['COUNT', 'MISSING_COUNT', 'PRODUCT_PRICE', 'CONFIRMED'].includes(column.key) && column.label.includes('product')) {
        rowValue = Number(rowValue);
    }

    if (options?.formatDisplay && shouldFormatDisplayNumber(column) && rowValue !== '' && isNumericValue(rowValue)) {
        rowValue = formatNumber(rowValue);
    }

    return rowValue;
};

export const getHeaderLabel = (column: TableColumnType, columsLabelPrefix?: string): string =>
    (columsLabelPrefix ? columsLabelPrefix + '.' : '') + column.label;

export const getBoolLikeColoredElement = (
    column: TableColumnType,
    row: TableRowType,
    selectOptions?: SelectOptionsType,
    text?: string | number
): ReactNode | null => {
    if (
        column.type !== 'select' ||
        !column.selectOption ||
        !isBoolLikeSelectOption(column.selectOption) ||
        !selectOptions?.[column.selectOption]
    ) {
        return null;
    }

    const rowValue = row[column.displayKey || column.key];
    if (rowValue === '' || rowValue === null || rowValue === undefined) {
        return null;
    }

    const selectedOption = selectOptions[column.selectOption].find(
        (item) => String(item.value) === String(rowValue)
    );
    if (!selectedOption) {
        return null;
    }

    const label = text !== undefined ? String(text) : selectedOption.label;
    const optionStyle = colorOptionFce(selectedOption);
    if (!optionStyle) {
        return label;
    }

    return <span style={optionStyle as React.CSSProperties}>{label}</span>;
};

export const getRowElementText = (
    row: TableRowType,
    column: TableColumnType,
    customRender?: CustomRenderType,
    selectOptions?: SelectOptionsType,
    options?: FormatColumnOptions
) => {
    const hasCustomRender = !!(column.customRender && customRender);
    const customElement = hasCustomRender ? customRender(column, row) : undefined;
    const text =
        customElement && typeof customElement === 'string'
            ? customElement
            : formatColumnText(column, row, selectOptions, options);

    let element: ReactNode | string | null | undefined = customElement;
    const customRenderReturnedNothing =
        hasCustomRender &&
        (customElement === null || customElement === undefined || customElement === false);

    if (!hasCustomRender || customRenderReturnedNothing) {
        const coloredElement = getBoolLikeColoredElement(column, row, selectOptions, text);
        if (coloredElement !== null) {
            element = coloredElement;
        }
    }

    return { text, element };
};

export const exportData = async (
    type: ExportTypeType,
    filename: string,
    propsRows: TableRowType[],
    visilbeColumns: TableColumnType[],
    rowIdKey: string,
    getPrintRows?: GetAllPrintRowsType,
    selectedRows?: SelectRowType[],
    columsLabelPrefix?: string,
    customRender?: CustomRenderType,
    selectOptions?: SelectOptionsType,
    extendedColmns?: TableColumnType[]
) => {
    let rows = propsRows;

    if (!extendedColmns && selectedRows?.length) {
        rows = propsRows.filter((row) => selectedRows?.includes(String(row[rowIdKey])));
    } else if (getPrintRows) {
        rows = await getPrintRows({ page: 1, items: MAX_EXPORT_ITEMS }, !!extendedColmns);
    }

    const drawColumns = [...visilbeColumns, ...(extendedColmns || [])];

    const headerTexts = drawColumns.map((column) => translate(getHeaderLabel(column, columsLabelPrefix)));
    const rowsTexts = rows.map((row) =>
        drawColumns.map((column) => {
            const { text } = getRowElementText(row, column, customRender, selectOptions);
            return text;
        })
    );

    switch (type) {
        case 'CSV':
            downloadCSV([headerTexts, ...rowsTexts], filename);
            break;
        case 'XLSX':
        default:
            downloadXLSX([headerTexts, ...rowsTexts], filename);
            break;
    }
};

export const getStringOrArrayFilter = (
    textValue: string
): {
    value: SelectValueType | SelectValueType[] | TextFieldValueType;
    operator: FilterOperatorType;
    textValue?: string;
} => {
    if (textValue[0] === FILTER_ARR_START_CHAR && textValue[textValue.length - 1] === FILTER_ARR_END_CHAR) {
        return {
            textValue,
            value: textValue.replace(FILTER_ARR_START_CHAR, '').replace(FILTER_ARR_END_CHAR, '').split(','),
            operator: 'in'
        };
    } else {
        return { value: textValue, operator: 'like' };
    }
};

export const getIdOrArrayFilter = (
    textValue: string
): {
    value: SelectValueType | SelectValueType[] | TextFieldValueType;
    operator: FilterOperatorType;
    textValue?: string;
} => {
    if (textValue[0] === FILTER_ARR_START_CHAR && textValue[textValue.length - 1] === FILTER_ARR_END_CHAR) {
        return {
            textValue,
            value: textValue.replace(FILTER_ARR_START_CHAR, '').replace(FILTER_ARR_END_CHAR, '').split(','),
            operator: 'in'
        };
    } else if (textValue.includes('*') || textValue.includes('?') || textValue.includes('%')) {
        return { value: textValue, operator: 'like' };
    } else {
        return { value: textValue, operator: 'eq' };
    }
};

export const getStringFromArrayFilter = (value?: string | string[]): any => {
    if (Array.isArray(value)) {
        return `(${value.join(',')})`;
    } else {
        return value;
    }
};

export const exportDataGrid = async (
    type: ExportTypeType,
    filename: string,
    getGridParams: () => GridRequestType,
    requestUrl: string,
    visilbeColumns: TableColumnType[],
    columsLabelPrefix?: string,
    selectedRows?: SelectRowType[],
    selectOptions?: SelectOptionsType,
    extendedColmns?: TableColumnType[],
    extendOptions?: ExtendOptionsType
) => {
    const gridParams: GridRequestType = { ...getGridParams(), page: 1, items: MAX_EXPORT_ITEMS };
    const paramColumns = [...(gridParams.columns ?? [])];

    if (selectedRows?.length) {
        if (!gridParams.filters.length) {
            gridParams.filters = [[]];
        }
        gridParams.filters[0] = [
            ...gridParams.filters[0],
            { column: 'ID', operator: 'in', value: selectedRows.map((item) => String(item)) }
        ];
    }

    const visibleColumnDefs = [...visilbeColumns, ...(extendedColmns || [])];
    const visibleKeys = visibleColumnDefs.map((item) => item.key).filter(Boolean);

    // visibleColumns from Table state can be [] before useEffect; keep grid payload columns as fallback
    gridParams.columns = visibleKeys.length > 0 ? visibleKeys : paramColumns;

    const columnsForLabels =
        visibleColumnDefs.length > 0
            ? visibleColumnDefs
            : paramColumns.map((key) => ({ key, label: key } as TableColumnType));

    const translatedColumns: Record<string, string> = {};
    columnsForLabels.forEach((column) => {
        const headerLabel = getHeaderLabel(column, columsLabelPrefix);
        translatedColumns[column.key] = translate(headerLabel);
    });

    // Use the server-side export functionality
    try {
        await exportFile(type, filename, gridParams, requestUrl, translatedColumns);
    } catch (error) {
        console.error(`Error exporting ${type} file:`, error);
    }
};

export const getMaxHeight = (props: TablePropsType, size: WindowSizeType, mobile?: boolean) => {
    if (props.maxHeight) {
        return props.maxHeight;
    }

    if (size.height) {
        if (props.heightVariant) {
            switch (props.heightVariant) {
                case 'top-tab':
                    if (mobile) {
                        // return size.height - 230;
                        return size.height - 305;
                    }
                    // return size.height - 235;
                    return size.height - 275;
                case 'top-tab-2':
                    if (mobile) {
                        // return size.height - 287;
                        return size.height - 375;
                    }
                    // return size.height - 297;
                    return size.height - 337;
                default:
                    break;
            }
        }

        if (mobile) {
            // return size.height - 180;
            return size.height - 250;
        }
        // return size.height - 230;
        return size.height - 275;
    }

    return 640;
};

export const parseTableDirection = (direction: string): 'asc' | 'desc' => (direction === 'ASC' ? 'asc' : 'desc');

export const sortColumns = (columns: TableColumnType[], orderColumns: any): TableColumnType[] => {
    if (!orderColumns || !isArrayOfStrings(orderColumns)) {
        return [...columns];
    }
    return [...columns].sort((a, b) => {
        const indexA = orderColumns.indexOf(a.key);
        const indexB = orderColumns.indexOf(b.key);

        if (indexA === -1 && a.forceFirst) {
            return -1;
        }
        if (indexB === -1 && b.forceFirst) {
            return 1;
        }

        if (indexA === -1 && indexB === -1) {
            return -1;
        }

        if (indexA === -1) {
            return 1;
        }

        if (indexB === -1) {
            return -1;
        }

        return indexA - indexB;
    });
};

export function isArrayOfStrings(param: any): param is string[] {
    if (Array.isArray(param)) {
        return param.every((item) => typeof item === 'string');
    }
    return false;
}

export function isObjectOfBooleans(param: any): param is { [key: string]: boolean } {
    if (typeof param === 'object' && param !== null) {
        for (const key in param) {
            if (param.hasOwnProperty(key) && typeof param[key] !== 'boolean') {
                return false;
            }
        }
        return true;
    }
    return false;
}
